import React from "react";
import { Col } from "react-bootstrap";
import AggregateCard from "../../../components/AggregateCard";
import TooltipHelper from "../../../components/TooltipHelper";
import {
  POSITIVE_TEST_RATE,
  COLOR_POSITIVE,
  COLOR_DEATHS,
  COLOR_TESTED,
} from "../../../GlobalColors";
import {
  wholeNumberToString,
  longDate,
  roundFloat,
} from "../../../utils/DataConversion";

interface AggregateProps {
  data: App.Agg;
  currentFilter: App.CurrentFilter;
  dateFilter: boolean;
}

const AggregateMetrics = (props: AggregateProps) => {
  const { data, currentFilter, dateFilter } = props;

  return (
    <React.Fragment>
      <Col xs={12} className="d-flex">
        <h4>
          <span className="font-weight-bold">Newly Reported</span> Confirmed
          COVID-19 Counts
        </h4>
        <TooltipHelper
          toolTipClass="ml-2 mt-1"
          text="Indiana defines confirmed COVID-19 counts as cases, deaths, or tests with a positive PCR or antigen lab result."
        />
      </Col>
      <AggregateCard
        rowSpan={3}
        title={"New Tests Administered"}
        filterShowTooltip={currentFilter.filters.length > 0}
        filterTooltipText={`New Tests Filtered On ${currentFilter.displayName}`}
        toolTipText={
          "The number of individuals tested refers to the distinct number of individuals who have had a PCR or antigen test for COVID-19. Total number of tests administered refers to the total number of PCR or antigen tests that have been conducted and counts all PCR or antigen tests for a single individual who was tested multiple times. Tests are dated on the date the test was performed. These numbers are provisional and reflect only those reported to IDOH. Numbers should not be characterized as a comprehensive total."
        }
        metricValue={wholeNumberToString(data.newTestsAdmin)}
        metricColor={COLOR_TESTED}
        subMetric={
          currentFilter.filters.length === 0
            ? `${wholeNumberToString(data.newTests)} New Individuals Tested`
            : ""
        }
        startDate={longDate(data.newTestsStartEnd[0])}
        endDate={longDate(data.newTestsStartEnd[1])}
      />
      <AggregateCard
        rowSpan={3}
        title={"New Positive Cases"}
        filterShowTooltip={currentFilter.filters.length > 0}
        filterTooltipText={`New Positive Cases On ${currentFilter.displayName}`}
        toolTipText={
          "The number of positive cases and statewide reinfection cases are provisional and reflect only those reported to IDOH. Case date refers to the date in which a positive PCR or antigen test was recorded in the IDOH system. Statewide reinfection cases refer to reinfection cases reported on or after September 1, 2021."
        }
        metricValue={wholeNumberToString(data.newCases)}
        metricColor={COLOR_POSITIVE}
        startDate={longDate(data.newCasesStartEnd[0])}
        endDate={longDate(data.newTestsStartEnd[1])}
      />
      <AggregateCard
        rowSpan={3}
        title={"Positivity - All Tests"}
        filterShowTooltip={currentFilter.filters.length > 0}
        filterTooltipText={`Total Percent Positive Filtered On ${currentFilter.displayName}`}
        toolTipText={`This method of calculating positivity rate is based on all positive test results and all tests processed. 
          The rate shows what percentage of all PCR and antigen tests on a given day were positive for COVID-19. For this calculation, the dates for both positive test results and all tests is based on the specimen collection date. 
          The 7-day average is reported with a 6-day lag to allow time to receive comprehensive results. 
          More information on the rate calculation methodology is provided under Additional Resources at the top of the dashboard.`}
        metricValue={`${data.allMovingAverage} %`}
        metricSubText={"7-Day Rate"}
        subMetric={`${roundFloat(
          (data.testsPositiveAdmin / data.cumTestAdmin) * 100,
          1
        )} % cumulative rate`}
        metricColor={POSITIVE_TEST_RATE}
        startDate={longDate(data.movingStart)}
        endDate={longDate(data.movingEnd)}
        subMetricDateFilteredEnabled={dateFilter}
      />
      <AggregateCard
        rowSpan={3}
        title={"New Deaths"}
        filterShowTooltip={currentFilter.filters.length > 0}
        filterTooltipText={`New Deaths Filtered On ${currentFilter.displayName}`}
        toolTipText={
          "New Reported Deaths refers to deaths newly reported to the Indiana Department of Health today. The date range indicates when these deaths occured. 7-Day Average refers to the average number of deaths occurring each day, by date of death, over the past seven day. All death numbers are provisional and reflect only those reported to IDOH with a confirmed positive COVID-19 test. Numbers should not be characterized as a comprehensive total and are subject to review."
        }
        metricValue={wholeNumberToString(data.newDeaths)}
        metricSubText={"Newly Reported Deaths"}
        subMetric={`7-Day Average - ${wholeNumberToString(
          data.deathMoving
        )} Deaths`}
        metricColor={COLOR_DEATHS}
        startDate={longDate(data.newDeathsStartEnd[0])}
        endDate={longDate(data.newDeathsStartEnd[1])}
        datePreText="Date of death between"
      />
      <Col xs={12} className="d-flex">
        <h4>
          <span className="font-weight-bold">Total</span> Confirmed COVID-19
          Counts
        </h4>
        <TooltipHelper
          toolTipClass="ml-2 mt-1"
          text="Indiana defines confirmed COVID-19 counts as cases, deaths, or tests with a positive PCR or antigen lab result."
        />
      </Col>
      <AggregateCard
        rowSpan={3}
        title={"Total Tests Administered"}
        filterShowTooltip={currentFilter.filters.length > 0}
        filterTooltipText={`Total Tests Filtered On ${currentFilter.displayName}`}
        toolTipText={
          "The number of individuals tested refers to the distinct number of individuals who have had a PCR or antigen test for COVID-19. Total number of tests administered refers to the total number of PCR or antigen tests that have been conducted and counts all PCR or antigen tests for a single individual who was tested multiple times. Tests are dated on the date the test was performed. These numbers are provisional and reflect only those reported to IDOH. Numbers should not be characterized as a comprehensive total."
        }
        metricValue={wholeNumberToString(data.testsAdmin)}
        metricColor={COLOR_TESTED}
        subMetric={`${wholeNumberToString(data.tests)} Individuals Tested`}
        startDate={longDate(data.testsStartEnd[0])}
        endDate={
          data.newTestsStartEnd
            ? longDate(data.newTestsStartEnd[1])
            : longDate(data.testsStartEnd[1])
        }
        metricDateFilteredEnabled={dateFilter}
      />
      <AggregateCard
        rowSpan={3}
        title={"Total Positive Cases"}
        filterShowTooltip={currentFilter.filters.length > 0}
        filterTooltipText={`Total Positive Cases Filtered On ${currentFilter.displayName}`}
        toolTipText={
          "The number of positive cases and statewide reinfection cases are provisional and reflect only those reported to IDOH. Case date refers to the date in which a positive PCR or antigen test was recorded in the IDOH system. Statewide reinfection cases refer to reinfection cases reported on or after September 1, 2021."
        }
        metricValue={wholeNumberToString(data.cases)}
        metricColor={COLOR_POSITIVE}
        startDate={longDate(data.casesStartEnd[0])}
        endDate={
          data.newCasesStartEnd
            ? longDate(data.newCasesStartEnd[1])
            : longDate(data.casesStartEnd[1])
        }
        subMetric={
          data.reinfections
            ? `${wholeNumberToString(
                data.reinfections
              )} Statewide Reinfection Cases since 9/1/2021`
            : ""
        }
        metricDateFilteredEnabled={dateFilter}
      />
      <AggregateCard
        rowSpan={3}
        title={"Positivity - Unique Individuals"}
        filterShowTooltip={currentFilter.filters.length > 0}
        filterTooltipText={`7-Day Positivity Rate Filtered On ${currentFilter.displayName}`}
        toolTipText={`This method of calculating positivity rate is based on unique individuals.  
          The rate shows the average over a 7-day period of the percentage of unique individuals who tested positive with COVID-19 each day. 
          For this calculation, the dates for both positive individuals and the individuals tested is based on the specimen collection date. 
          The 7-day average is reported with a 6-day lag to allow time to receive comprehensive results.
          More information on the rate calculation methodology is provided under Additional Resources at the top of the dashboard.`}
        metricValue={`${data.movingAverage} %`}
        metricSubText={"7-Day Rate"}
        subMetric={`${roundFloat(
          ((data.cases - (data.reinfections || 0)) / data.tests) * 100,
          1
        )} % cumulative rate`}
        metricColor={POSITIVE_TEST_RATE}
        startDate={longDate(data.movingStart)}
        endDate={longDate(data.movingEnd)}
        subMetricDateFilteredEnabled={dateFilter}
      />
      <AggregateCard
        rowSpan={3}
        title={"Total Deaths"}
        filterShowTooltip={currentFilter.filters.length > 0}
        filterTooltipText={`Total Deaths Filtered On ${currentFilter.displayName}`}
        toolTipText={
          "Number of deaths is provisional and reflects only those reported to IDOH with a confirmed positive COVID-19 test. Numbers should not be characterized as a comprehensive total."
        }
        metricValue={wholeNumberToString(data.deaths)}
        metricColor={COLOR_DEATHS}
        startDate={longDate(data.deathsStartEnd[0])}
        endDate={
          data.newDeathsStartEnd
            ? longDate(data.newDeathsStartEnd[1])
            : longDate(data.deathsStartEnd[1])
        }
        metricDateFilteredEnabled={dateFilter}
      />
    </React.Fragment>
  );
};

export default AggregateMetrics;
